<section
  class="relative z-50 flex min-h-[calc(100vh-220px)] justify-center px-4 py-12 lg:px-8"
  aria-label="Login account transition"
>
  <div class="relative w-full max-w-xl">
    <div
      class="relative mx-auto w-full max-w-2xl rounded-2xl bg-white p-8 text-black shadow-xl md:p-10"
    >
      <!-- Logo -->
      <div class="mb-8 flex">
        <a
          routerLink="/"
          class="inline-block"
          aria-label="Comcast Business — go to homepage"
        >
          <img
            src="assets/images/logos/CB_Logo_Color_RGB.png"
            alt=""
            role="presentation"
            class="h-16 w-auto"
          />
        </a>
      </div>

      <div aria-live="polite" aria-atomic="true">
        <h2 id="step-heading" tabindex="-1" class="text-2xl font-semibold">
          @if (currentStep() === 'form') {
            Secure your account in one step
          } @else if (currentStep() === 'confirmation') {
            Check your email
          } @else {
            You're all set
          }
        </h2>
      </div>

      @if (currentStep() === 'form') {
        <div class="font-lato mt-2">
          <p class="text-gray-650 mx-auto leading-6">
            We're moving to an industry-standard login platform — so your
            account stays protected with the best available security.
          </p>

          <div class="border-gray-150 bg-blue-75 mt-6 rounded-xl border p-4">
            <p class="text-lg font-semibold text-gray-900">Why update now</p>

            <ul class="text-gray-650 mt-3 space-y-2">
              <li class="flex items-start gap-2">
                <span
                  class="mt-[6px] h-2 w-2 rounded-full bg-blue-700"
                  aria-hidden="true"
                ></span>
                Multi-factor authentication built in
              </li>
              <li class="flex items-start gap-2">
                <span
                  class="mt-[6px] h-2 w-2 rounded-full bg-blue-700"
                  aria-hidden="true"
                ></span>
                Faster, simpler password recovery
              </li>
              <li class="flex items-start gap-2">
                <span
                  class="mt-[6px] h-2 w-2 rounded-full bg-blue-700"
                  aria-hidden="true"
                ></span>
                Invite and manage team members
              </li>
              <li class="flex items-start gap-2">
                <span
                  class="mt-[6px] h-2 w-2 rounded-full bg-blue-700"
                  aria-hidden="true"
                ></span>
                Required for MyPortal access after October 2026
              </li>
            </ul>
          </div>

          <form
            [formGroup]="formGroup"
            class="mt-8"
            novalidate
            (ngSubmit)="submit()"
          >
            <label
              for="transitionEmailId"
              class="comcast-input-label text-base"
            >
              Email address
            </label>

            <input
              formControlName="email"
              id="transitionEmailId"
              type="email"
              required
              name="email"
              autocomplete="email"
              aria-describedby="email-error"
              [attr.aria-invalid]="messages().emailInput.length > 0"
              [ngClass]="{
                'comcast-input-invalid': messages().emailInput.length > 0,
              }"
              class="comcast-input transition focus:border-blue-700 focus:ring-2 focus:ring-blue-700"
              placeholder="name@company.com"
            />

            <div aria-live="polite">
              @if (messages().emailInput.length > 0) {
                <span id="email-error" class="comcast-validation-error">
                  {{ messages().emailInput }}
                </span>
              }
            </div>

            @if (messages().emailInput.length === 0) {
              <p class="mt-1 text-sm leading-5 text-gray-600">
                This becomes your new login username. You'll get a confirmation
                email to activate.
              </p>
            }

            @if (errorMessage()) {
              <div
                class="comcast-validation-error mt-4 text-base"
                role="alert"
                aria-live="polite"
              >
                {{ errorMessage() }}
              </div>
            }

            <div
              class="mt-4 w-full"
              [ngClass]="{ 'mt-4': errorMessage(), 'mt-8': !errorMessage() }"
            >
              <button
                type="submit"
                class="btn-primary w-full justify-center shadow-md transition hover:shadow-lg"
                [disabled]="isLoading()"
                [attr.aria-busy]="isLoading()"
                [attr.aria-label]="
                  isLoading() ? 'Submitting, please wait' : 'Confirm email'
                "
              >
                @if (isLoading()) {
                  <div class="flex min-w-12 justify-center">
                    <mat-spinner
                      [diameter]="20"
                      aria-hidden="true"
                    ></mat-spinner>
                  </div>
                } @else {
                  {{ errorMessage() ? 'Try again' : 'Confirm email' }}
                }
              </button>

              <button
                type="button"
                class="btn-secondary mt-2 w-full justify-center opacity-80 transition hover:opacity-100"
                [disabled]="isLoading()"
                routerLink="/dashboard"
              >
                I'll do this later
              </button>
            </div>

            <p class="mt-6 text-center text-sm leading-5 text-gray-400">
              By continuing, you agree to the Comcast Business
              <a
                href="https://business.comcast.com/enterprise/terms-conditions/nitel"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Terms of Service (opens in new tab)"
                class="font-lato text-sm"
                >Terms of Service</a
              >
              and
              <a
                href="https://www.xfinity.com/privacy/policy"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Privacy Policy (opens in new tab)"
                class="font-lato text-sm"
                >Privacy Policy.
              </a>
              and applicable terms.
            </p>
          </form>
        </div>
      } @else if (currentStep() === 'federated') {
        <!-- ── Federated / SSO success screen ── -->
        <div class="font-lato mt-2">
          <p class="text-gray-650 leading-6">
            Your account has been migrated. Since your organization uses single
            sign-on (SSO), there's nothing else you need to do &mdash; no
            password setup, no activation email.
          </p>

          <!-- Email / username card — matches confirmation screen style -->
          <div
            class="bg-blue-75 my-8 rounded-xl border border-blue-100 p-4"
            role="status"
            aria-label="Migration successful"
          >
            <p class="flex items-center gap-2 text-xl leading-6 text-gray-800">
              <span
                class="h-2.5 w-2.5 shrink-0 rounded-full bg-green-500"
                aria-hidden="true"
              ></span>
              <span class="font-bold">{{ activationEmail() }}</span>
            </p>
          </div>

          <!-- What's next -->
          <ul class="mt-2 space-y-3" aria-label="Next steps">
            <li class="flex gap-3">
              <div class="flex flex-col items-center" aria-hidden="true">
                <span
                  class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200 font-medium text-gray-800"
                  >1</span
                >
                <div class="mt-1 w-px grow bg-gray-200"></div>
              </div>
              <span class="text-gray-650 pb-4 leading-7">
                Sign in at MyPortal using your
                <strong class="font-semibold text-gray-800"
                  >company SSO credentials</strong
                >
                &mdash; same as you do today.
              </span>
            </li>
            <li class="flex gap-3">
              <div class="flex flex-col items-center" aria-hidden="true">
                <span
                  class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200 font-medium text-gray-800"
                  >2</span
                >
                <div class="mt-1 w-px grow bg-gray-200"></div>
              </div>
              <span class="text-gray-650 pb-4 leading-6">
                No new password or MFA enrollment required &mdash; your
                organization handles authentication.
              </span>
            </li>
            <li class="flex gap-3">
              <div class="flex flex-col items-center" aria-hidden="true">
                <span
                  class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200 font-medium text-gray-800"
                  >3</span
                >
              </div>
              <span class="text-gray-650 pb-4 leading-6">
                All your existing account access and settings are preserved.
              </span>
            </li>
          </ul>

          <button
            type="button"
            class="btn-primary mt-6 w-full justify-center shadow-md"
            (click)="signOut()"
          >
            Sign in to your account
          </button>

          <p class="mt-3 text-center text-sm text-gray-400">
            You'll be signed out automatically in
            <strong>{{ migrationTimer.countdown() }}s</strong>
          </p>
        </div>
      } @else if (currentStep() === 'confirmation') {
        <div class="font-lato mt-2">
          <p>
            We sent a link to activate your new login. Takes about 2 minutes to
            finish setup.
          </p>

          <div
            class="bg-blue-75 my-8 rounded-xl border border-blue-100 p-4"
            role="status"
            [attr.aria-label]="'Activation email sent to ' + activationEmail()"
          >
            <p class="flex items-center gap-2 text-xl leading-6 text-gray-800">
              <span
                class="h-2.5 w-2.5 shrink-0 rounded-full bg-green-500"
                aria-hidden="true"
              ></span>
              <span class="font-bold" aria-hidden="true">{{
                activationEmail()
              }}</span>
            </p>
          </div>

          <ol aria-label="Steps to complete your login setup">
            <li class="flex gap-3">
              <div class="flex flex-col items-center" aria-hidden="true">
                <span
                  class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200 font-medium text-gray-800"
                  >1</span
                >
                <div class="mt-1 w-px grow bg-gray-200"></div>
              </div>
              <span class="text-gray-650 pb-4 leading-7"
                >Open the email from
                <strong class="font-semibold text-gray-800"
                  >noreply&#64;ni.comcast.com</strong
                >
                and click
                <strong class="font-semibold text-gray-800"
                  >Activate Comcast account</strong
                ></span
              >
            </li>
            <li class="flex gap-3">
              <div class="flex flex-col items-center" aria-hidden="true">
                <span
                  class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200 font-medium text-gray-800"
                  >2</span
                >
                <div class="mt-1 w-px grow bg-gray-200"></div>
              </div>
              <span class="text-gray-650 pb-4 leading-6">
                <strong class="font-semibold text-gray-800"
                  >Create a new password</strong
                >
                for your login
                <br />
                <span class="text-sm text-gray-400"
                  >You won't use your old MyPortal password after this.</span
                >
              </span>
            </li>
            <li class="flex gap-3">
              <div class="flex flex-col items-center" aria-hidden="true">
                <span
                  class="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-gray-200 font-medium text-gray-800"
                  >3</span
                >
              </div>
              <span class="text-gray-650 pb-4 leading-6">
                <strong class="font-semibold text-gray-800"
                  >Set up multi-factor authentication (MFA)</strong
                >
                <br />
                <span class="text-sm text-gray-400"
                  >Use an authenticator app &mdash; keeps your account secure
                  every time you sign in.</span
                >
              </span>
            </li>
          </ol>

          @if (errorMessage()) {
            <div
              class="comcast-validation-error mt-4 text-base"
              role="alert"
              aria-live="polite"
            >
              {{ errorMessage() }}
            </div>
          }

          <div class="mt-4 flex w-full items-baseline justify-between gap-4">
            <p class="font-medium text-gray-400">Didn't receive the email?</p>
            <button
              type="button"
              class="btn-secondary rounded-none border-0 px-2"
              [disabled]="isLoading()"
              [attr.aria-busy]="isLoading()"
              [attr.aria-label]="
                isLoading() ? 'Sending email, please wait' : 'Resend email'
              "
              (click)="resendEmail()"
            >
              @if (isLoading()) {
                <div class="flex min-w-12 justify-center px-2">
                  <mat-spinner
                    [diameter]="20"
                    aria-hidden="true"
                    class="[&_circle]:!stroke-black"
                  ></mat-spinner>
                </div>
              } @else {
                Resend email
                <mat-icon aria-hidden="true">email</mat-icon>
              }
            </button>
          </div>
          <p class="-mt-2 text-sm text-gray-400">
            Check your spam folder if you don't see it within a minute.
          </p>

          <button
            type="button"
            class="btn-secondary mt-8 w-full justify-center"
            [disabled]="isLoading()"
            (click)="signOut()"
          >
            Done
          </button>

          <p class="mt-2 text-center text-sm text-gray-400">
            You'll be logged out automatically in
            <strong>{{ migrationTimer.countdown() }}s</strong>
          </p>
        </div>
      }
    </div>
  </div>
</section>
